// @ts-nocheck
import { compact, uniq } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import {
  formatTimeFrame,
  getCartographyFilter,
  getDateFormatted,
  getMetricsKeys,
  NUMBER_DAYS_MONTH,
  NUMBER_HOURS_DAY
} from '@/helpers/reporting.helpers';
import { reportingSetKpiStats, reportingSetKpiStatsStatus } from '@/store/actions/reporting.actions';
import { getBaseCurrencyName } from '@/store/selectors/baseCurrency.selectors';
import {
  getKpiStatsStatus,
  getReportingKpiStats,
  getSelectedCampaigns,
  getStaticMode
} from '@/store/selectors/reporting.selectors';
import {
  getCartographyCountries,
  getCartographyLevel,
  getCartographyLevelIds,
  getChartTimeStep,
  getCurrency,
  getTimeFrame,
  getVariableType,
  getVariableValues
} from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { getCampaignsType } from '@/ts-helpers/campaign.helpers';
import { fetchProgressStats, getProgressStatsRequestBody } from '@/ts-resources/reporting/reportingProgress.resources';

type Props = {
  cids: string[];
  country: string;
  disaggregated: string;
  disaggregatedBy?: string;
  groupBy: string;
  hideKpis?: boolean;
  metricsKpis: {
    kpi?: {
      id: string;
      label: string;
    };
    metric: {
      id: string;
      label: string;
    };
  };
  showAllLegend?: boolean;
  timezone?: string;
};

export function useReportingEventsChart({
  cids,
  country,
  disaggregated,
  disaggregatedBy,
  groupBy,
  hideKpis = false,
  metricsKpis,
  showAllLegend = false,
  timezone = 'UTC'
}: Props) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const kpiStatsToken: Record<string, any> = {};

  const user = useSelector(getCurrentUser);
  const currency = useSelector(store => getCurrency(store));
  const baseCurrency = useSelector(getBaseCurrencyName);
  const { beginDate, endDate, hasCustomDates } = useSelector(getTimeFrame);
  // @ts-ignore
  const kpiStats: Record<string, any> = useSelector(store => getReportingKpiStats(store, groupBy));
  // @ts-ignore
  const kpiStatsStatus = useSelector(store => getKpiStatsStatus(store, groupBy));
  const generalCartographyCountries = useSelector(getCartographyCountries);
  const generalCartographyLevel = useSelector(getCartographyLevel);
  const generalCartographyLevelIds = useSelector(getCartographyLevelIds);
  // @ts-ignore
  const cartographyFilter: Record<string, string[]> | undefined = getCartographyFilter({
    cartographyCountries: generalCartographyCountries,
    cartographyLevel: generalCartographyLevel,
    cartographyLevelIds: generalCartographyLevelIds
  });
  const variableType = useSelector(store => getVariableType(store));
  const variableValues = useSelector(store => getVariableValues(store));
  const variable = variableType && variableValues.length ? { [variableType]: variableValues } : undefined;
  const chartTimeStep = useSelector(store => getChartTimeStep(store));
  const campaigns: Record<string, any>[] = useSelector(getSelectedCampaigns);
  const campaignType = getCampaignsType(campaigns);
  const useBackups = useSelector(getStaticMode);

  useEffect(() => {
    return () => {
      kpiStatsToken && kpiStatsToken.cancel && kpiStatsToken.cancel();
    };
  }, []);

  const formattedTimeFrame = formatTimeFrame(
    {
      end: endDate,
      start: beginDate
    },
    timezone,
    undefined,
    undefined,
    hasCustomDates
  );
  const dateInterval = {
    ...formattedTimeFrame,
    timezone
  };

  const requestBody = getProgressStatsRequestBody(
    {
      baseCurrency,
      currency
    },
    dateInterval,
    groupBy,
    compact([metricsKpis.kpi?.id, metricsKpis.metric.id]),
    cartographyFilter,
    variable,
    useBackups
  );

  const canRequestStats = (() => {
    const noStats = !kpiStats || (disaggregatedBy && !kpiStats[disaggregatedBy]);
    const requestingNeeded = kpiStatsStatus !== 'pending';
    const { kpi, metric } = metricsKpis;
    return !!noStats && requestingNeeded && (!!kpi?.id || campaignType !== CampaignFamilyEnum.standard) && !!metric?.id;
  })();

  const fetchStats = async () => {
    if (canRequestStats) {
      try {
        dispatch(reportingSetKpiStatsStatus(groupBy, 'pending'));
        const selectedCampaignIds: string[] = uniq(campaigns.map(campaign => campaign.id));
        const statsPromises = [
          fetchProgressStats(
            user.id,
            selectedCampaignIds,
            chartTimeStep,
            'impressionsKpisChart',
            requestBody,
            useBackups,
            kpiStatsToken
          )
        ];
        if (disaggregatedBy) {
          statsPromises.push(
            fetchProgressStats(
              user.id,
              selectedCampaignIds,
              chartTimeStep,
              'impressionsKpisChart',
              {
                ...requestBody,
                params: {
                  ...requestBody.params,
                  groupOptions: [
                    requestBody.params.groupOptions[0],
                    {
                      ...requestBody.params.groupOptions[0],
                      field: disaggregatedBy
                    }
                  ]
                }
              },
              useBackups,
              kpiStatsToken
            )
          );
        }
        const [stats, disaggregateByStats] = await Promise.all(statsPromises);
        dispatch(
          reportingSetKpiStats({
            groupBy,
            grouped: stats[0],
            ...(disaggregatedBy && {
              [disaggregatedBy]: disaggregateByStats[0]
            })
          })
        );
        dispatch(reportingSetKpiStatsStatus(groupBy, 'finished'));
      } catch (_e) {
        dispatch(reportingSetKpiStatsStatus(groupBy, 'error'));
      }
    }
  };

  useEffect(() => {
    fetchStats();
  }, [groupBy, kpiStats, disaggregatedBy, metricsKpis]);

  const sorted = (categories: string[]): string[] => {
    if (['day', 'hour', 'week', 'month'].includes(groupBy)) {
      categories.sort((a, b) => {
        return moment.utc(a).valueOf() - moment.utc(b).valueOf();
      });
    }
    return categories;
  };

  const categories: string[] = useMemo(() => {
    if (!kpiStats || !kpiStats.grouped) {
      return [];
    }
    if (groupBy === 'hour') {
      let statsCategory = kpiStats.grouped.map((s: Record<string, any>) => s[groupBy]);
      statsCategory = sorted(statsCategory);
      return statsCategory;
    }
    if (groupBy === 'day') {
      let statsCategory = kpiStats.grouped.map((s: Record<string, any>) => s[groupBy]);
      statsCategory = uniq(statsCategory);
      statsCategory = sorted(statsCategory);
      statsCategory = statsCategory.slice(-NUMBER_DAYS_MONTH);
      return statsCategory;
    }
    if (groupBy === 'dow') {
      return ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    }
    if (['week', 'month'].includes(groupBy)) {
      return sorted(uniq(kpiStats.grouped.map((s: Record<string, any>) => s[groupBy])));
    }
    return [...new Array(NUMBER_HOURS_DAY).keys()].map(s => s.toString());
  }, [kpiStats]);

  const getCategories = () => {
    if (['dow', 'hod'].includes(groupBy)) {
      return categories;
    }
    return categories.map((date: string) => {
      return getDateFormatted(groupBy, date, false);
    });
  };

  const getXAxisTitle = () => {
    if (['hod', 'hour'].includes(groupBy)) {
      return formatMessage({
        defaultMessage: 'Hours',
        id: 'reporting.impressionsKpisChart.graphTitles.xAxisTitle.hours'
      });
    }
    return formatMessage({
      defaultMessage: `${groupBy}s`,
      id: `reporting.impressionsKpisChart.graphTitles.xAxisTitle.${groupBy}s`
    });
  };

  const getMetricValue = (
    stats: Record<string, any>[],
    disaggregatedId: string,
    metricId: string | undefined,
    grouped: boolean
  ): (number | null)[] => {
    const key = getMetricsKeys(metricId);
    return categories.map(category => {
      const kpiStat = stats.find(stat => {
        return stat[groupBy] === category && (grouped || stat[`${disaggregatedBy}Id`] === disaggregatedId);
      });
      const number = kpiStat && kpiStat[key] !== undefined ? kpiStat[key] : null;
      if (number === null) {
        return null;
      }
      return Math.round(number * 100) / 100;
    });
  };

  const getAverage = (stats: Record<string, any>[], cid: string, id: string): string | null => {
    const kpiData = getMetricValue(stats, cid, id, true);
    const kpiDataFiltered: number[] = compact(kpiData.filter(kpi => kpi !== null));
    if (kpiDataFiltered.length === 0) {
      return null;
    }
    return (kpiDataFiltered.reduce((acc, cur) => acc + cur) / kpiDataFiltered.length).toFixed(2);
  };

  const getLegendFormatter = (value: string) => {
    const legendText = value.split('|');
    if (!showAllLegend) {
      return legendText[legendText.length - 1];
    }
    return legendText[0];
  };

  const getPlotLine = () => {
    if (!kpiStats || !kpiStats.grouped || !metricsKpis?.kpi?.id) {
      return null;
    }
    return [
      {
        color: 'rgba(223,173,61,0.4)',
        label: {
          text: 'AVG'
        },
        value: getAverage(kpiStats.grouped, cids[0], metricsKpis.kpi?.id),
        width: 1
      }
    ];
  };

  const configStats = useMemo(() => {
    if (!kpiStats || !kpiStats.grouped || !metricsKpis) {
      return null;
    }

    let internalColumnStats = kpiStats.grouped;
    let internalLinesStats = kpiStats.grouped;

    let disaggregatedByItems = [];
    let columnItemIds = ['grouped'];
    let linesItemIds = ['grouped'];
    const groupedColumn = ['none', 'kpi'].includes(disaggregated);
    const groupedLines = ['none', 'metric'].includes(disaggregated);
    if (disaggregatedBy && kpiStats[disaggregatedBy]) {
      disaggregatedByItems = [
        ...new Set(
          kpiStats[disaggregatedBy].map((item: Record<string, any>) => {
            return item[`${disaggregatedBy}Id`];
          })
        )
      ];
      if (['both', 'kpi'].includes(disaggregated)) {
        linesItemIds = disaggregatedByItems as string[];
        internalLinesStats = kpiStats[disaggregatedBy];
      }
      if (['both', 'metric'].includes(disaggregated)) {
        columnItemIds = disaggregatedByItems as string[];
        internalColumnStats = kpiStats[disaggregatedBy];
      }
    }

    const columns = columnItemIds.map(id => {
      const entry = disaggregatedBy
        ? internalColumnStats.find((ics: Record<string, any>) => {
            return ics[`${disaggregatedBy}Id`] === id;
          })
        : null;
      return {
        data: getMetricValue(internalColumnStats, id, metricsKpis.metric.id, groupedColumn),
        name:
          entry && disaggregatedBy && entry[disaggregatedBy]
            ? `${entry[disaggregatedBy]} | ${metricsKpis.metric.label}`
            : metricsKpis.metric.label,
        type: 'column',
        yAxis: hideKpis ? 0 : 1
      };
    });
    const lines = !hideKpis
      ? linesItemIds.map(id => {
          const entry = disaggregatedBy
            ? internalLinesStats.find((ics: Record<string, any>) => {
                return ics[`${disaggregatedBy}Id`] === id;
              })
            : null;
          return {
            data: getMetricValue(internalLinesStats, id, metricsKpis.kpi?.id, groupedLines),
            lineWidth: 2,
            name:
              entry && disaggregatedBy && entry[disaggregatedBy]
                ? `${entry[disaggregatedBy]} | ${metricsKpis.kpi?.label}`
                : metricsKpis.kpi?.label,
            type: 'line',
            yAxis: 0
          };
        })
      : [];
    const multiTypeSeries = [...columns, ...lines];
    return {
      multiTypeSeries,
      xAxisConfig: {
        categories: getCategories(),
        title: getXAxisTitle()
      },
      yAxisConfigs: [
        ...(hideKpis
          ? []
          : [
              {
                plotLines: getPlotLine(),
                title: metricsKpis.kpi?.label
              }
            ]),
        {
          ...(!hideKpis && { opposite: true }),
          title: metricsKpis.metric.label
        }
      ]
    };
  }, [metricsKpis, kpiStats, disaggregated, disaggregatedBy]);

  const chartConfig = {
    country,
    legend: true,
    legendFormatter: getLegendFormatter,
    series: configStats?.multiTypeSeries,
    showAllLegend,
    type: 'multiType',
    xAxisConfig: configStats?.xAxisConfig,
    yAxisConfigs: configStats?.yAxisConfigs
  };

  return {
    chartConfig,
    configStats,
    isReady: kpiStatsStatus === 'finished',
    kpiStatsStatus
  };
}
